<template>
  <div
    :class="$b()"
    :style="styles"
  >
    <img
      v-for="layer in layers"
      :key="`${layer.id}-${frameNumber}`"
      :src="layer.currentFramePath"
      :class="
        $b('img', {
          secondary: layer.isSecondary,
          iris: layer.isIrisMask,
          irisVisible: layer.isIrisMask && irisMaskVisible,
          frame: frameNumber,
          unpadded,
        })
      "
      :style="{
        ...imgStyles,
        opacity: layer.opacity || false,
        '--iris-visible-opacity': layer.isIrisMask && (layer.maxOpacity || 0.3),
      }"
      @load="layersLoaded = layersLoaded + 1"
    />

    <div
      v-if="showLimitedEditionLogo"
      :class="$b('limited-edition-logo')"
    >
      <DatoCmsImage
        v-if="limitedEditionLogo"
        :key="limitedEditionLogo.responsiveImage.src"
        :data="{
          ...limitedEditionLogo.responsiveImage,
          sizes: '(max-width: 800px) 50vw, 400px',
        }"
        :lazy-load="false"
      />
    </div>
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'
import Layer from '~/models/Layer'
import allLayerTemplates from '~/assets/360/all.mjs'
import { Image as DatoCmsImage } from 'vue-datocms'

const noop = () => {}

export default {
  name: 'StaticThreeSixtyFrame',
  components: {
    DatoCmsImage,
  },
  props: {
    customisedProduct: { type: CustomisedProduct, required: true },
    showIrisPreview: { type: Boolean, default: false },
    frameNumber: { type: Number, default: 1 },
    showBackground: { type: Boolean, default: false },
    showReflections: { type: Boolean, default: false },
    width: { type: Number, default: 1400 },
    height: { type: Number, default: 1400 },
    scale: { type: Number, default: 100 },
    xOffset: { type: Number, default: 0 },
    yOffset: { type: Number, default: 0 },
    showLimitedEditionLogo: { type: Boolean, default: false },
    imageSizeOverride: { type: Number, default: 0 },
    showSecondary: { type: Boolean, default: false },
    unpadded: { type: Boolean, default: false }
  },
  emits: ['loaded'],
  data: () => ({
    frameCount: 72,
    selectedOptions: {},
    selectedSecondaryOptions: {},
    layersLoaded: 0,
  }),
  computed: {
    styles() {
      return {
        maxWidth: `${this.width}px`,
        maxHeight: `${this.height}px`,
      }
    },
    imgStyles() {
      // Return strings not decimal to avoid scales being dropped weirdly
      return {
        '--tw-translate-x': `calc(${this.xOffset}% - 50% + var(--x-offset))`,
        '--tw-translate-y': `calc(${this.yOffset}% - 50% + var(--y-offset))`,
        '--tw-scale-x': `calc(${this.scale} / 100)`,
        '--tw-scale-y': `calc(${this.scale} / 100)`,
      }
    },
    layerTemplate() {
      return allLayerTemplates[this.customisedProduct.product.sku]
    },
    layers() {
      const getSelectedOption = (layerId) => {
        if (
          layerId in this.selectedOptions &&
          this.selectedOptions[layerId] !== 'none'
        ) {
          return this.selectedOptions[layerId]
        } else if (
          layerId in this.selectedSecondaryOptions &&
          this.selectedSecondaryOptions[layerId] !== 'none'
        ) {
          return this.selectedSecondaryOptions[layerId]
        } else if (layerId === 'background') {
          return 'default'
        }

        return null
      }

      const layers = this.layerTemplate.layers.map(
        (layer) =>
          new Layer(
            {
              ...layer,
              selectedOption: getSelectedOption(layer.id),
              isSecondary:
                layer.id in this.selectedSecondaryOptions &&
                this.selectedSecondaryOptions[layer.id] !== 'none',
              frameCount: this.frameCount,
              frameNumber: this.frameNumber,
              useMobileImages: false,
              imageSizeOverride: this.imageSizeOverride,
              useStaticPathTemplate: true
            },
            false
          )
      )

      const filteredLayers = layers.filter((layer) => {
        if (layer.isBackground) return this.showBackground && !!layer.currentFramePath
        if (layer.isReflection) return false
        if (
          layer.currentFrameDisabled ||
          (layer.hasOptions && layer.selectedOption === 'none')
        )
          return false
        if (
          layer.isSecondary &&
          (!this.showSecondary || !this.customisedProduct.conversionKitEnabled)
        )
          return false
        return true
      })

      return filteredLayers
    },
    limitedEditionLogo() {
      return this.customisedProduct.product.limitedEditionLogo
    },
    irisMaskVisible() {
      return this.showIrisPreview && this.customisedProduct.isIrisLens
    },
  },
  watch: {
    'customisedProduct._hash'() {
      this.resolveOptions()
    },
    layersLoaded() {
      if (this.layersLoaded === this.layers.length) {
        this.$emit('loaded')
      }
    },
  },
  created() {
    this.resolveOptions()
  },
  mounted() {
    this.resolveOptions = this.throttleFunction(this.resolveOptions, 5)
  },
  methods: {
    resolveOptions() {
      this.selectedOptions = this.layerTemplate.resolveOptions(
        this.customisedProduct
      )
      this.selectedSecondaryOptions =
        (this.layerTemplate.resolveSecondaryOptions || noop)(
          this.customisedProduct
        ) || {}
    },
  },
}
</script>

<style lang="postcss">
.StaticThreeSixtyFrame {
  @apply h-full w-full overflow-hidden;
}

.StaticThreeSixtyFrame__img {
  --x-offset: 0%;
  --y-offset: 0%;
  --iris-visible-opacity: 0.3;

  @apply absolute
    top-1/2 left-1/2 transform
    mdMax:pb-sm;

  &--unpadded {
    @apply mdMax:pb-0;
  }

  &--iris {
    @apply transition-opacity duration-1000 opacity-0;

    &-visible {
      @apply opacity-[var(--iris-visible-opacity)];
    }
  }

  &--secondary {
    --x-offset: 10%;
    --y-offset: 40%;
    --tw-scale-x: calc(95 / 100) !important;
    --tw-scale-y: calc(95 / 100) !important;
  }

  &--frame-1 {
    @apply mdMax:pb-0;
  }
}

.StaticThreeSixtyFrame__limited-edition-logo {
  @apply absolute w-1/4
    bottom-[40px] right-sm lg:bottom-md lg:right-md;

  picture {
    @apply object-cover;
  }
}
</style>
